import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Alert, Form, Input, Typography } from 'antd';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/Button';
import { ROUTES } from '@/routes';
import { parseClerkError } from '@/utils';
import styles from './ResetPassword.module.scss';

interface ResetPasswordFormValues {
  email: string;
}

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, isLoaded } = useSignIn();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    if (!signIn || !isLoaded) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: values.email,
      });
      navigate(ROUTES.RESET_PASSWORD_CONFIRM, { state: { email: values.email } });
    } catch (err) {
      setError(parseClerkError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Link to={ROUTES.LOGIN} className={styles.backLink}>
        <ArrowLeft style={{ width: '1em', height: '1em' }} /> Back to login
      </Link>

      <Typography.Title level={1} className={styles.title}>
        Reset your password
      </Typography.Title>
      <Typography.Text className={styles.subtitle}>
        Enter the email address associated with your account and we&rsquo;ll send you a code to
        reset your password.
      </Typography.Text>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.alert}
        />
      )}

      <Form<ResetPasswordFormValues>
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="on"
        requiredMark={false}
        colon={false}
        className={styles.form}
        initialValues={{ email: '' }}
      >
        <Form.Item
          name="email"
          label="Email address"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            placeholder="you@example.com"
            size="large"
            autoComplete="email"
            disabled={isSubmitting}
          />
        </Form.Item>

        <Button appearance="solid" htmlType="submit" size="large" block loading={isSubmitting} disabled={!isLoaded}>
          Send reset code
        </Button>
      </Form>
    </div>
  );
};

export default ResetPassword;
